import type { ReactNode } from 'react';

/**
 * The menu as a tree of any depth. A node with `children` is a branch (a group, folded in the sidebar); a branch with a
 * `path` also has its own page (its dashboard). A node without children is a page and needs a `path`, or an action
 * (`onSelect`, no `path`).
 */
export interface NavNode {
  /** Stable id, unique in the tree (fold state, test hooks); defaults to `path` (see `nodeId`). */
  id?: string;
  label: string;
  path?: string;
  /** Scope needed to see and open the node (`svc:*` wildcards honoured); none = everyone signed in. */
  requires?: string;
  /** Further scopes needed as well (all of them). */
  alsoRequires?: string[];
  /** One line for a dashboard card. */
  description?: string;
  /**
   * The link is active on its exact path only (default). `false` keeps a top-level page highlighted on its sub-routes
   * (`/orders` while on `/orders/42`).
   */
  end?: boolean;
  /** Shown before the label in the menu (an 18 px lucide icon); decorative, the label names the node. */
  icon?: ReactNode;
  /** An action instead of a page: a leaf without `path` is a button that runs this (opens a picker, say). */
  onSelect?: () => void;
  children?: NavNode[];
}

/** Decides whether the caller may see one node by its own rule (its ancestors and children are not consulted). */
export type NavAllow = (node: NavNode) => boolean;

export const nodeId = (node: NavNode): string => node.id ?? node.path ?? node.label;

export const isBranch = (node: NavNode): boolean => !!node.children?.length;

/**
 * The part of the tree the caller may see: a page or an action when allowed, a branch when allowed and at least one
 * of its children is visible (a branch with nothing left under it disappears, even with a page of its own).
 */
export function visibleTree(tree: readonly NavNode[], allow: NavAllow): NavNode[] {
  const out: NavNode[] = [];
  for (const node of tree) {
    if (!allow(node)) continue;
    if (!node.children) {
      if (node.path || node.onSelect) out.push(node);
      continue;
    }
    const children = visibleTree(node.children, allow);
    if (children.length) out.push({ ...node, children });
  }
  return out;
}

/** Every node, depth first in screen order. */
export function flatten(tree: readonly NavNode[]): NavNode[] {
  return tree.flatMap((n) => [n, ...flatten(n.children ?? [])]);
}

/** The pages (nodes with a path, branches with a page included). */
export const pages = (tree: readonly NavNode[]): NavNode[] => flatten(tree).filter((n) => !!n.path);

export const findNode = (tree: readonly NavNode[], id: string): NavNode | undefined =>
  flatten(tree).find((n) => nodeId(n) === id);

/** May the caller open `path`: it is the path of a node in the visible tree. */
export const canOpenPath = (tree: readonly NavNode[], allow: NavAllow, path: string): boolean =>
  pages(visibleTree(tree, allow)).some((n) => n.path === path);

export const isOnPath = (path: string, pathname: string): boolean =>
  pathname === path || pathname.startsWith(path + '/');

/**
 * The nodes from the root down to the one holding `pathname`: the node with the longest matching path (deeper wins a
 * tie), so a page under a group page without its own node belongs to that group. Empty when nothing matches.
 */
export function findTrail(tree: readonly NavNode[], pathname: string): NavNode[] {
  let best: NavNode[] = [];
  let bestLen = -1;
  const walk = (nodes: readonly NavNode[], trail: NavNode[]) => {
    for (const node of nodes) {
      const here = [...trail, node];
      if (node.path && isOnPath(node.path, pathname) && node.path.length >= bestLen) {
        best = here;
        bestLen = node.path.length;
      }
      if (node.children) walk(node.children, here);
    }
  };
  walk(tree, []);
  return best;
}

/** The nodes from the root down to the one with id `id` (`nodeId`); empty when no node has it. */
export function findTrailById(tree: readonly NavNode[], id: string): NavNode[] {
  for (const node of tree) {
    if (nodeId(node) === id) return [node];
    const below = findTrailById(node.children ?? [], id);
    if (below.length) return [node, ...below];
  }
  return [];
}
